import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import logoWhite from '../assets/flipad-logo-white.svg';
import logoFull from '../assets/flipad-logo.svg';
import { openApp, playStoreUrl } from '../lib/appUrl.js';

// Desktop mega-menu content is identical on every page in the original site
// (same Platform / Formats / Resources dropdowns) — only two things vary per
// page, both passed in as props by the page that renders <Header>:
//   - mobileLinks: the page's own mobile-nav-links list (each page links to
//     its own on-page sections rather than a single generic list).
//   - activeNavKey: which dropdown item gets the "is-active" treatment
//     ('advertisers' | 'space-owners' | 'billboard' | null).
export default function Header({ mobileLinks, activeNavKey = null }) {
  // Mirrors the original's nav-logged-out / nav-logged-in toggle — a demo of
  // what the nav looks like signed in, not real auth (this is a static
  // marketing site with no backend).
  const [loggedInDemo, setLoggedInDemo] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const sheetRef = useRef(null);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <nav className="site-nav" id="site-nav" aria-label="Site navigation">
        <div className="container">
          <div className="nav-inner">
            <Link to="/" className="nav-logo" aria-label="FlipAd home">
              <img className="logo-white" src={logoWhite} alt="FlipAd" />
              <img className="logo-dark" src={logoFull} alt="FlipAd" />
            </Link>

            <div className="nav-links" role="list">
              {/* Platform */}
              <div className="nav-item" role="listitem">
                <button className="nav-link" type="button" aria-haspopup="true">
                  Platform <i className="ph ph-caret-down nav-caret-icon" aria-hidden="true" />
                </button>
                <div className="nav-dd nav-dd-wide" role="menu">
                  <div className="nav-dd-plat-cols">
                    <div className="nav-dd-plat-col">
                      <Link to="/advertisers" className={`nav-dd-plat-head nav-dd-plat-adv${activeNavKey === 'advertisers' ? ' is-active' : ''}`} role="menuitem">
                        <div className="nav-dd-icon"><i className="ph ph-megaphone" /></div>
                        <div><div className="nav-dd-title">For Advertisers</div><div className="nav-dd-desc">Discover and book OOH inventory</div></div>
                        <i className="ph ph-arrow-right nav-dd-arrow" />
                      </Link>
                      <Link to="/advertisers#booking-steps" className="nav-dd-item" role="menuitem">
                        <div className="nav-dd-icon"><i className="ph ph-magnifying-glass" /></div>
                        <div><div className="nav-dd-title">Discover Inventory</div><div className="nav-dd-desc">Search 1,240+ verified spaces across India</div></div>
                      </Link>
                      <Link to="/advertisers#booking-steps" className="nav-dd-item" role="menuitem">
                        <div className="nav-dd-icon"><i className="ph ph-calendar-check" /></div>
                        <div><div className="nav-dd-title">Book Campaigns</div><div className="nav-dd-desc">Confirm in minutes, pay through escrow</div></div>
                      </Link>
                      <Link to="/advertisers#disc-preview" className="nav-dd-item" role="menuitem">
                        <div className="nav-dd-icon"><i className="ph ph-chart-line-up" /></div>
                        <div><div className="nav-dd-title">Track Performance</div><div className="nav-dd-desc">Proof of display, impressions, campaign logs</div></div>
                      </Link>
                    </div>
                    <div className="nav-dd-plat-sep" aria-hidden="true" />
                    <div className="nav-dd-plat-col">
                      <Link to="/space-owners" className={`nav-dd-plat-head nav-dd-plat-host${activeNavKey === 'space-owners' ? ' is-active' : ''}`} role="menuitem">
                        <div className="nav-dd-icon"><i className="ph ph-house-line" /></div>
                        <div><div className="nav-dd-title">For Space Owners</div><div className="nav-dd-desc">List your site and earn from bookings</div></div>
                        <i className="ph ph-arrow-right nav-dd-arrow" />
                      </Link>
                      <Link to="/space-owners#listing-steps" className="nav-dd-item" role="menuitem">
                        <div className="nav-dd-icon"><i className="ph ph-map-pin" /></div>
                        <div><div className="nav-dd-title">List Your Space</div><div className="nav-dd-desc">Free to list — discovered by brands nationwide</div></div>
                      </Link>
                      <Link to="/space-owners#dashboard" className="nav-dd-item" role="menuitem">
                        <div className="nav-dd-icon"><i className="ph ph-calendar-dots" /></div>
                        <div><div className="nav-dd-title">Manage Bookings</div><div className="nav-dd-desc">Calendar, requests, block dates — all in one place</div></div>
                      </Link>
                      <Link to="/space-owners#escrow" className="nav-dd-item" role="menuitem">
                        <div className="nav-dd-icon"><i className="ph ph-currency-inr" /></div>
                        <div><div className="nav-dd-title">Earn &amp; Get Paid</div><div className="nav-dd-desc">Transparent payouts, escrow-protected earnings</div></div>
                      </Link>
                    </div>
                  </div>
                  <div className="nav-dd-plat-footer">
                    <span className="nav-dd-plat-stat"><i className="ph ph-map-pin" /><strong>1,240+</strong>&nbsp;verified spaces</span>
                    <span className="nav-dd-plat-stat"><i className="ph ph-shield-check" /><strong>100%</strong>&nbsp;escrow-protected</span>
                    <span className="nav-dd-plat-stat"><i className="ph ph-buildings" /><strong>23</strong>&nbsp;cities</span>
                  </div>
                </div>
              </div>
              {/* Formats */}
              <div className="nav-item" role="listitem">
                <button className="nav-link" type="button" aria-haspopup="true">
                  Formats <i className="ph ph-caret-down nav-caret-icon" aria-hidden="true" />
                </button>
                <div className="nav-dd nav-dd-fmts" role="menu">
                  <Link to="/billboard" className={`nav-dd-item${activeNavKey === 'billboard' ? ' is-active' : ''}`} role="menuitem">
                    <div className="nav-dd-icon"><i className="ph ph-signpost" /></div>
                    <div><div className="nav-dd-title">Billboard</div><div className="nav-dd-desc">Classic large-format roadside</div></div>
                  </Link>
                  <Link to="/#formats" className="nav-dd-item" role="menuitem">
                    <div className="nav-dd-icon"><i className="ph ph-monitor-play" /></div>
                    <div><div className="nav-dd-title">Digital Screen</div><div className="nav-dd-desc">LED and LCD dynamic content</div></div>
                  </Link>
                  <Link to="/#formats" className="nav-dd-item" role="menuitem">
                    <div className="nav-dd-icon"><i className="ph ph-bus" /></div>
                    <div><div className="nav-dd-title">Transit Panel</div><div className="nav-dd-desc">Buses, metro and railways</div></div>
                  </Link>
                  <Link to="/#formats" className="nav-dd-item" role="menuitem">
                    <div className="nav-dd-icon"><i className="ph ph-broadcast" /></div>
                    <div><div className="nav-dd-title">Unipole</div><div className="nav-dd-desc">Tall single-pillar highway sites</div></div>
                  </Link>
                  <Link to="/#formats" className="nav-dd-item" role="menuitem">
                    <div className="nav-dd-icon"><i className="ph ph-bench" /></div>
                    <div><div className="nav-dd-title">Street Furniture</div><div className="nav-dd-desc">Bus shelters, kiosks and pillars</div></div>
                  </Link>
                  <Link to="/#formats" className="nav-dd-item" role="menuitem">
                    <div className="nav-dd-icon"><i className="ph ph-buildings" /></div>
                    <div><div className="nav-dd-title">Commercial</div><div className="nav-dd-desc">Malls, airports and elevators</div></div>
                  </Link>
                  <div className="nav-dd-footer">
                    <Link to="/#formats">All formats <i className="ph ph-arrow-right" style={{ fontSize: 11 }} /></Link>
                  </div>
                </div>
              </div>
              {/* Resources */}
              <div className="nav-item" role="listitem">
                <button className="nav-link" type="button" aria-haspopup="true">
                  Resources <i className="ph ph-caret-down nav-caret-icon" aria-hidden="true" />
                </button>
                <div className="nav-dd" role="menu">
                  <Link to="/blog" className="nav-dd-item" role="menuitem">
                    <div className="nav-dd-icon"><i className="ph ph-article" /></div>
                    <div><div className="nav-dd-title">Insights</div><div className="nav-dd-desc">OOH market data, guides, and playbooks</div></div>
                  </Link>
                  <Link to="/#faq" className="nav-dd-item" role="menuitem">
                    <div className="nav-dd-icon"><i className="ph ph-question" /></div>
                    <div><div className="nav-dd-title">FAQ</div><div className="nav-dd-desc">Common questions about FlipAd</div></div>
                  </Link>
                  <a href="#" className="nav-dd-item" role="menuitem" onClick={(e) => e.preventDefault()}>
                    <div className="nav-dd-icon"><i className="ph ph-headset" /></div>
                    <div><div className="nav-dd-title">Help Centre</div><div className="nav-dd-desc">Platform guides and support</div></div>
                  </a>
                </div>
              </div>
            </div>

            <div className="nav-ctas">
              {!loggedInDemo ? (
                <div id="nav-logged-out" style={{ display: 'flex', alignItems: 'center', gap: 'var(--lumen-space-2)' }}>
                  {/* Cross-app navigation, not a client-side <Link>: the real
                      login page lives in the main FlipAd app, a separate
                      BrowserRouter root outside this marketing site's own.
                      Opens in a new tab so the marketing site stays put. */}
                  <a href={playStoreUrl()} target="_blank" rel="noopener noreferrer" className="lmn-btn ghost-light sm">Sign in</a>
                  <button type="button" className="lmn-btn primary sm" onClick={() => openApp('/inventory')}>Browse inventory</button>
                </div>
              ) : (
                <div id="nav-logged-in" style={{ display: 'flex', alignItems: 'center', gap: 'var(--lumen-space-2)' }}>
                  <a href="#" className="lmn-btn ghost-light sm" onClick={(e) => e.preventDefault()}>My campaigns</a>
                  <div className="nav-profile" id="nav-profile-btn" role="button" tabIndex={0} aria-label="Account menu" onClick={() => setLoggedInDemo(false)}>
                    <div className="nav-avatar">RA</div>
                    <i className="ph ph-caret-down nav-profile-caret" aria-hidden="true" />
                  </div>
                </div>
              )}
              <button
                className="nav-hamburger"
                id="nav-hamburger"
                aria-label="Open menu"
                aria-expanded={mobileOpen}
                aria-controls="mobile-nav"
                onClick={() => setMobileOpen((v) => !v)}
              >
                <i className={mobileOpen ? 'ph ph-x' : 'ph ph-list'} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile nav */}
      <div
        className={`mobile-nav${mobileOpen ? ' is-open' : ''}`}
        id="mobile-nav"
        aria-hidden={!mobileOpen}
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        ref={sheetRef}
        onClick={(e) => { if (e.target === sheetRef.current) closeMobile(); }}
      >
        <div className="mobile-nav-panel">
          <nav className="mobile-nav-links" aria-label="Mobile navigation">
            {mobileLinks.map((l) => (
              <Link key={l.href} to={l.href} className="mobile-nav-link" onClick={closeMobile}>{l.label}</Link>
            ))}
          </nav>
          <div className="mobile-nav-rule" aria-hidden="true" />
          <div className="mobile-nav-ctas">
            <a href={playStoreUrl()} target="_blank" rel="noopener noreferrer" className="lmn-btn outline-white md" style={{ justifyContent: 'center' }}>Sign in</a>
            <button type="button" className="lmn-btn primary md" style={{ justifyContent: 'center' }} onClick={() => openApp('/inventory')}>Browse inventory</button>
          </div>
        </div>
      </div>
    </>
  );
}

// The generic mobile-nav-links list used by pages that don't have their own
// dedicated set of on-page sections (blog.html, blog-article.html).
export const DEFAULT_MOBILE_LINKS = [
  { href: '/advertisers', label: 'For Advertisers' },
  { href: '/space-owners', label: 'For Space Owners' },
  { href: '/#formats', label: 'Formats' },
  { href: '/blog', label: 'Insights' },
  { href: '/#faq', label: 'FAQ' },
];
